// src/features/collaboration/normalizationUtils.ts

/**
 * テキスト正規化ユーティリティ
 * 日本語検索の向上のため、ひらがな統一・記号統一・空白正規化を行う
 */

/**
 * カタカナをひらがなに変換
 */
export const katakanaToHiragana = (text: string): string => {
  return text.replace(/[\u30A1-\u30F6]/g, (match) => {
    return String.fromCharCode(match.charCodeAt(0) - 0x60);
  });
};

/**
 * 漢字の読み仮名変換は複雑なため、現在は対象外
 * 将来的にはWanaKanaやkuromoji.jsなどのライブラリを使用予定
 */

/**
 * 基本的なテキスト正規化
 */
export const normalizeText = (input: string): string => {
  if (!input || typeof input !== 'string') return '';
  
  return input
    .normalize('NFKC')        // 全角→半角・合成文字の正規化
    .replace(/\r\n|\r/g, '\n') // 改行統一（LF）
    .replace(/\s+/g, ' ')      // 連続空白を1つのスペースに統一
    .replace(/　+/g, ' ')      // 全角スペースも統一
    .trim();                   // 前後空白除去
};

/**
 * 日本語検索用の正規化（ひらがな統一）
 */
export const normalizeForSearch = (input: string): string => {
  if (!input || typeof input !== 'string') return '';
  
  const basicNormalized = normalizeText(input);
  
  return basicNormalized
    .toLowerCase()             // 英字を小文字に統一
    .replace(/[ａ-ｚＡ-Ｚ]/g, (match) => {
      // 全角英字を半角に変換
      return String.fromCharCode(match.charCodeAt(0) - 0xFEE0);
    })
    .replace(/[０-９]/g, (match) => {
      // 全角数字を半角に変換
      return String.fromCharCode(match.charCodeAt(0) - 0xFEE0);
    })
    .replace(/[！-～]/g, (match) => {
      // 全角記号を半角に変換（基本的なもの）
      const code = match.charCodeAt(0);
      // 全角英数記号の範囲（！から～まで）を半角に変換
      if (code >= 0xFF01 && code <= 0xFF5E) {
        return String.fromCharCode(code - 0xFEE0);
      }
      return match;
    })
    .replace(/[\u30A1-\u30F6]/g, (match) => {
      // カタカナをひらがなに変換
      return String.fromCharCode(match.charCodeAt(0) - 0x60);
    })
    .replace(/[・]/g, '')      // 中点を除去
    .replace(/[ー]/g, '')      // 長音符を除去
    .replace(/\s+/g, ' ')      // 再度空白を統一
    .trim();
};

/**
 * タイトル正規化（基本正規化のみ）
 */
export const normalizeTitle = (title: string): string => {
  return normalizeText(title);
};

/**
 * タイトル検索用正規化（ひらがな統一）
 */
export const normalizeTitleForSearch = (title: string): string => {
  return normalizeForSearch(title);
};

/**
 * コンテンツ正規化（基本正規化のみ）
 */
export const normalizeContent = (content: string): string => {
  return normalizeText(content);
};

/**
 * コンテンツ検索用正規化（ひらがな統一）
 */
export const normalizeContentForSearch = (content: string): string => {
  return normalizeForSearch(content);
};

/**
 * HTMLタグを除去してプレーンテキストに変換
 */
export const stripHtmlTags = (html: string): string => {
  if (!html || typeof html !== 'string') return '';
  
  return html
    .replace(/<[^>]*>/g, ' ')    // HTMLタグを除去
    .replace(/&nbsp;/g, ' ')     // &nbsp;をスペースに変換
    .replace(/&lt;/g, '<')       // HTMLエンティティを変換
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')        // 連続空白を統一
    .trim();
};

/**
 * 検索用データ生成のためのテキスト正規化
 * HTMLタグ除去 → 基本正規化 → 検索用正規化
 */
export const prepareTextForSearch = (text: string): {
  original: string;
  normalized: string;
} => {
  if (!text || typeof text !== 'string') {
    return {
      original: '',
      normalized: ''
    };
  }
  
  // HTMLタグが含まれている場合は除去
  const plainText = stripHtmlTags(text);
  const original = normalizeText(plainText);
  const normalized = normalizeForSearch(plainText);
  
  return {
    original,
    normalized
  };
};

/**
 * 検索用キーワード抽出（将来的な拡張用）
 */
export const extractSearchKeywords = (text: string): string[] => {
  if (!text || typeof text !== 'string') return [];
  
  const normalizedText = normalizeForSearch(text);
  
  // 基本的な分割（スペース、句読点区切り）
  const keywords = normalizedText
    .split(/[\s、。！？,.!?]+/)
    .filter(keyword => keyword.length >= 2) // 2文字以上のキーワードのみ
    .filter(keyword => keyword.trim())
    .map(keyword => keyword.trim());
  
  // 重複除去
  return Array.from(new Set(keywords));
};

/**
 * 正規化処理のログ出力（デバッグ用）
 */
export const logNormalizationResult = (
  original: string, 
  normalized: string, 
  label: string = 'text'
): void => {
  if (import.meta.env.MODE === 'development') {}
};
